// src/components/CompleteProfile.js
import React, { useState, useEffect } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { userService } from '../services/userService';
import { auth } from '../firebase';
import countryList from '../utils/countryList';

function CompleteProfile({ user }) {
  const navigate = useNavigate();
  const [country, setCountry] = useState('');
  const [agreeTerms, setAgreeTerms] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [initialLoad, setInitialLoad] = useState(true);
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  
  useEffect(() => {
    const fetchUserData = async () => {
      if (!user) return;
      
      try {
        // Refresh token first thing
        await auth.currentUser?.getIdToken(true);
        
        // Try to get user profile using our service
        const userData = await userService.getUserProfile(user.uid);
        
        if (userData) {
          // Pre-fill form fields with existing data
          if (userData.firstName) setFirstName(userData.firstName);
          if (userData.lastName) setLastName(userData.lastName);
          if (userData.country) setCountry(userData.country);
          if (userData.licenseAgreed) setAgreeTerms(userData.licenseAgreed);
          
          // If user has already completely filled their profile, redirect to documents
          if (userData.licenseAgreed && userData.country) {
            navigate("/documents");
          }
        } else {
          // No user profile exists yet, pre-fill with auth data if available
          if (user.displayName) {
            const nameParts = user.displayName.split(' ');
            setFirstName(nameParts[0] || '');
            setLastName(nameParts.slice(1).join(' ') || '');
          }
        }
      } catch (error) {
        console.error("Error loading profile:", error);
        setErrorMessage("Unable to load profile data: " + error.message);
      } finally {
        setInitialLoad(false);
      }
    };
    
    fetchUserData();
  }, [user, navigate]);
  
  if (!user) {
    return <Navigate to="/login" />;
  }
  
  const handleReauthenticate = () => {
    // Log the user out
    auth.signOut().then(() => {
      // Redirect to login with a return path
      navigate('/login?return=complete-profile');
    }).catch(error => {
      console.error("Error signing out:", error);
    });
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrorMessage('');
    setLoading(true);
    
    if (!agreeTerms) {
      setErrorMessage('You must agree to the Non-Commercial License to continue');
      setLoading(false);
      return;
    }
    
    if (!country) {
      setErrorMessage('Please select your country');
      setLoading(false);
      return;
    }
    
    try {
      // Use our userService to complete the profile
      await userService.completeProfile(user.uid, {
        firstName,
        lastName,
        email: user.email || '',
        country,
        licenseAgreed: true
      });
      
      // Success! Redirect to documents
      navigate("/documents");
    } catch (error) {
      console.error("Error updating profile:", error);
      setErrorMessage("Error updating profile: " + error.message);
      setLoading(false);
    }
  };
  
  // Mobile-optimized styles (same as before)
  const mobileInputStyle = {
    fontSize: '16px',
    height: '56px',
    width: '100%',
    padding: '0 16px',
    borderRadius: '8px',
    border: '1px solid #d1d5db',
    appearance: 'none'
  };
  
  const mobileSelectStyle = {
    ...mobileInputStyle,
    paddingRight: '40px',
    backgroundImage: `url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='currentColor' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3e%3cpolyline points='6 9 12 15 18 9'%3e%3c/polyline%3e%3c/svg%3e")`,
    backgroundRepeat: 'no-repeat',
    backgroundPosition: 'right 16px center',
    backgroundSize: '16px'
  };
  
  const mobileButtonStyle = {
    fontSize: '18px',
    fontWeight: '600',
    height: '56px',
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: '8px',
    border: 'none',
    padding: '0 16px',
    appearance: 'none',
    cursor: 'pointer'
  };
  
  return (
    <div className="container mx-auto px-4 py-6">
      <div className="text-center mb-6">
        <h1 className="text-2xl font-bold">Complete Your Profile</h1>
      </div>
      
      {errorMessage && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4 text-center">
          <p className="font-bold">Error:</p>
          <p>{errorMessage}</p>
          {errorMessage.includes("permission") && (
            <div className="mt-4">
              <p className="mb-2">This may be due to an authentication issue. Please try logging in again.</p>
              <button
                onClick={handleReauthenticate}
                className="bg-red-600 text-white font-bold py-2 px-4 rounded hover:bg-red-700"
              >
                Log out and sign in again
              </button>
            </div>
          )}
        </div>
      )}
      
      <div className="bg-blue-100 p-4 rounded-lg mb-6">
        <h2 className="text-xl font-semibold mb-2">Almost There!</h2>
        <p>
          Before accessing DIY documentation, please complete your profile information and agree to our license terms.
        </p>
      </div>
      
      {initialLoad ? (
        <div className="flex justify-center items-center p-8">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-600"></div>
          <span className="ml-3">Loading your profile information...</span>
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="bg-gray-100 p-5 rounded-lg shadow-md max-w-md mx-auto">
          {/* Form fields remain the same */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <div>
              <label className="block text-gray-700 mb-2 text-lg" htmlFor="firstName">First Name</label>
              <input
                type="text"
                id="firstName"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                style={mobileInputStyle}
                required
              />
            </div>
            <div>
              <label className="block text-gray-700 mb-2 text-lg" htmlFor="lastName">Last Name</label>
              <input
                type="text"
                id="lastName"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                style={mobileInputStyle}
                required
              />
            </div>
          </div>
          
          <div className="mb-4">
            <label className="block text-gray-700 mb-2 text-lg" htmlFor="country">Country</label>
            <select
              id="country"
              value={country}
              onChange={(e) => setCountry(e.target.value)}
              style={mobileSelectStyle}
              required
            >
              <option value="">Select a country</option>
              {countryList.map((countryName, index) => (
                <option key={index} value={countryName}>
                  {countryName}
                </option>
              ))}
            </select>
          </div>
          
          <div className="mb-6">
            <div className="flex items-start">
              <input
                type="checkbox"
                id="agreeTerms"
                checked={agreeTerms}
                onChange={(e) => setAgreeTerms(e.target.checked)}
                className="mt-1 mr-3 h-6 w-6" // Larger checkbox for mobile
                required
              />
              <label htmlFor="agreeTerms" className="text-base">
                I have read and agree to the <a 
                  href="/license" 
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-600 hover:underline"
                >
                  Waka Folding Technology Non-Commercial License
                </a>. I understand that:
                <ul className="list-disc ml-5 mt-2">
                  <li className="mb-1">I am limited to building up to three (3) boards per calendar year for personal use only</li>
                  <li className="mb-1">I cannot sell or commercialize boards built using this technology</li>
                  <li className="mb-1">Any improvements I make to the technology must be shared with Waka</li>
                  <li className="mb-1">I recognize the validity of the pending patent application for this technology</li>
                </ul>
              </label>
            </div>
          </div>
          
          <button 
            type="submit" 
            disabled={loading}
            style={{
              ...mobileButtonStyle,
              backgroundColor: '#2563eb',
              color: 'white'
            }}
          >
            {loading ? (
              <><span className="mr-2">Submitting...</span><div className="animate-spin h-5 w-5 border-2 border-white rounded-full border-t-transparent"></div></>
            ) : "Complete Registration"}
          </button>
        </form>
      )}
    </div>
  );
}

export default CompleteProfile;